(function () {
  'use strict';

  function debounce(func, wait) {
    let timeout;
    return function executedFunction(...args) {
      const later = () => {
        clearTimeout(timeout);
        func(...args);
      };
      clearTimeout(timeout);
      timeout = setTimeout(later, wait);
    };
  }

  function escapeHtml(text) {
    if (text == null) return '';
    const div = document.createElement('div');
    div.textContent = text;
    return div.innerHTML;
  }

  function levenshtein(a, b) {
    const m = a.length, n = b.length;
    const dp = [];
    for (let i = 0; i <= m; i++) {
      dp[i] = [i];
      for (let j = 1; j <= n; j++) {
        dp[i][j] = i === 0 ? j : 0;
      }
    }
    for (let i = 1; i <= m; i++) {
      for (let j = 1; j <= n; j++) {
        if (a[i - 1] === b[j - 1]) {
          dp[i][j] = dp[i - 1][j - 1];
        } else {
          dp[i][j] = 1 + Math.min(dp[i - 1][j], dp[i][j - 1], dp[i - 1][j - 1]);
        }
      }
    }
    return dp[m][n];
  }

  function fuzzyScore(cityName, query) {
    const name = cityName.toLowerCase();
    const q = query.toLowerCase();
    if (name === q) return 100;
    if (name.startsWith(q)) return 80;
    if (name.includes(q)) return 60;
    // Subsequence match (all query chars appear in order)
    let qi = 0;
    for (let i = 0; i < name.length && qi < q.length; i++) {
      if (name[i] === q[qi]) qi++;
    }
    if (qi === q.length) return 40;
    // Levenshtein distance for typo tolerance
    const threshold = q.length <= 3 ? 1 : q.length <= 5 ? 2 : 3;
    const dist = levenshtein(name, q);
    if (dist <= threshold) return Math.max(1, 30 - dist * 5);
    // Partial Levenshtein: compare query against substrings of name
    for (let start = 0; start <= name.length - q.length + threshold; start++) {
      const sub = name.substring(start, start + q.length + threshold);
      const subDist = levenshtein(sub.substring(0, Math.min(sub.length, q.length + 1)), q);
      if (subDist <= threshold) return Math.max(1, 20 - subDist * 5);
    }
    return 0;
  }

  function sanitizeUrl(url) {
    if (!url || typeof url !== 'string') return '';
    const trimmed = url.trim();
    if (trimmed.toLowerCase().startsWith('javascript:') ||
      trimmed.toLowerCase().startsWith('data:') ||
      trimmed.toLowerCase().startsWith('vbscript:')) {
      return '#';
    }
    if (!/^(https?|mailto|tel):/i.test(trimmed)) {
      return '#';
    }
    return trimmed;
  }

  class SearchModule {
    constructor(container, cities, onSelect, apiKey) {
      this.container = container;
      this.cities = cities;
      this.onSelect = onSelect;
      this.apiKey = apiKey || null;
      this.input = container.querySelector('.store-search-input');
      this.dropdown = container.querySelector('.store-search-dropdown');
      this.clearBtn = container.querySelector('.store-search-icon-btn');
      this.searchIcon = container.querySelector('.store-search-icon-default-btn');
      this.selectedIndex = -1;
      this.filteredCities = [];
      this.filteredScores = [];
      this.autocompleteService = null;
      this.documentClickHandler = null;
      this.init();
    }

    getAutocompleteService() {
      if (this.autocompleteService) return this.autocompleteService;
      if (window.google && window.google.maps && window.google.maps.places) {
        this.autocompleteService = new google.maps.places.AutocompleteService();
      }
      return this.autocompleteService;
    }

    searchWithPlaces(query) {
      const service = this.getAutocompleteService();
      if (!service) return;
      service.getPlacePredictions(
        { input: query, types: ['(cities)'], componentRestrictions: { country: 'in' } },
        (predictions, status) => {
          // Discard stale callbacks — input has changed since this query was sent
          if (this.input.value.trim().toLowerCase() !== query) return;
          if (status !== google.maps.places.PlacesServiceStatus.OK || !predictions) return;

          // Re-run local fuzzy fresh for this exact query (no stale state)
          const localScored = this.cities
            .map(city => ({ city, score: fuzzyScore(city.name, query) }))
            .filter(({ score }) => score > 0);

          // Extract city names from Google predictions
          const googleCityNames = predictions
            .slice(0, 5)
            .map(p => p.structured_formatting ? p.structured_formatting.main_text : p.description.split(',')[0].trim());

          // Build merged map seeded only from fresh local results
          const mergedMap = new Map();
          localScored.forEach(({ city, score }) => {
            mergedMap.set(city.handle, { city, score });
          });

          // Score each Google city name against stored cities and merge
          googleCityNames.forEach(googleName => {
            this.cities.forEach(storedCity => {
              const score = fuzzyScore(storedCity.name, googleName);
              if (score > 0) {
                const existing = mergedMap.get(storedCity.handle);
                // Boost Google-confirmed matches by 20 so they rank above pure local guesses
                const boostedScore = Math.min(100, score + 20);
                if (!existing || boostedScore > existing.score) {
                  mergedMap.set(storedCity.handle, { city: storedCity, score: boostedScore });
                }
              }
            });
          });

          const sorted = Array.from(mergedMap.values()).sort((a, b) => b.score - a.score);
          this.filteredCities = sorted.map(({ city }) => city);
          this.filteredScores = sorted.map(({ score }) => score);
          if (this.filteredCities.length > 0) {
            this.showDropdown();
            this.renderDropdown();
          } else {
            this.showDropdown();
            this.renderNoResults();
          }
        }
      );
    }

    init() {
      const debouncedInputHandler = debounce((e) => this.handleInput(e), 200);
      this.debouncedPlacesSearch = debounce((query) => this.searchWithPlaces(query), 400);

      this.input.addEventListener('input', (e) => {
        this.updateIconVisibility();
        debouncedInputHandler(e);
      });
      this.input.addEventListener('keydown', (e) => this.handleKeydown(e));
      this.input.addEventListener('focus', () => {
        this.updateIconVisibility();
        this.handleFocus();
      });
      this.input.addEventListener('click', () => {
        this.updateIconVisibility();
        this.handleFocus();
      });
      this.input.addEventListener('blur', () => {
        this.updateIconVisibility();
        setTimeout(() => this.hideDropdown(), 200);
      });
      this.input.addEventListener('paste', () => {
        setTimeout(() => this.updateIconVisibility(), 10);
      });

      if (this.searchIcon) {
        this.searchIcon.addEventListener('click', (e) => {
          e.preventDefault();
          e.stopPropagation();
          this.input.focus();
          this.handleFocus();
        });
      }

      if (this.clearBtn) {
        this.clearBtn.addEventListener('click', (e) => {
          e.preventDefault();
          e.stopPropagation();
          this.clear();
        });
      }

      this.documentClickHandler = (e) => {
        if (!this.container.contains(e.target)) {
          this.hideDropdown();
        }
      };
      document.addEventListener('click', this.documentClickHandler);

      this.dropdown.addEventListener('mousedown', (e) => {
        e.preventDefault();
      });

      this.updateIconVisibility();
    }

    updateIconVisibility() {
      const hasValue = this.input && this.input.value.trim().length > 0;
      if (this.clearBtn && this.searchIcon) {
        if (hasValue) {
          this.clearBtn.removeAttribute('hidden');
          this.clearBtn.style.display = 'flex';
          this.searchIcon.style.display = 'none';
        } else {
          this.clearBtn.setAttribute('hidden', '');
          this.clearBtn.style.display = 'none';
          this.searchIcon.style.display = 'flex';
        }
      }
    }

    handleInput(e) {
      const query = e.target.value.trim().toLowerCase();
      if (query.length === 0) {
        this.hideDropdown();
        return;
      }
      // Local fuzzy search — runs immediately
      const scored = this.cities
        .map(city => ({ city, score: fuzzyScore(city.name, query) }))
        .filter(({ score }) => score > 0)
        .sort((a, b) => b.score - a.score);
      this.filteredCities = scored.map(({ city }) => city);
      this.filteredScores = scored.map(({ score }) => score);
      if (this.filteredCities.length > 0) {
        this.showDropdown();
        this.renderDropdown();
      } else {
        this.showDropdown();
        this.renderNoResults();
      }
      // Google Places search — runs debounced (400ms), merges and re-renders
      if (this.apiKey && query.length >= 2) {
        this.debouncedPlacesSearch(query);
      }
    }

    handleKeydown(e) {
      if (!this.dropdown.hasAttribute('hidden')) {
        if (e.key === 'ArrowDown') {
          e.preventDefault();
          this.selectedIndex = Math.min(this.selectedIndex + 1, this.filteredCities.length - 1);
          this.updateDropdownSelection();
        } else if (e.key === 'ArrowUp') {
          e.preventDefault();
          this.selectedIndex = Math.max(this.selectedIndex - 1, -1);
          this.updateDropdownSelection();
        } else if (e.key === 'Enter') {
          e.preventDefault();
          if (this.selectedIndex >= 0 && this.filteredCities[this.selectedIndex]) {
            this.selectCity(this.filteredCities[this.selectedIndex]);
          }
        } else if (e.key === 'Escape') {
          this.hideDropdown();
          this.input.blur();
        }
      }
    }

    handleFocus() {
      const query = this.input.value.trim().toLowerCase();
      if (query.length === 0) {
        this.filteredCities = this.cities;
        this.filteredScores = this.cities.map(() => 100);
      } else {
        const scored = this.cities
          .map(city => ({ city, score: fuzzyScore(city.name, query) }))
          .filter(({ score }) => score > 0)
          .sort((a, b) => b.score - a.score);
        this.filteredCities = scored.map(({ city }) => city);
        this.filteredScores = scored.map(({ score }) => score);
      }
      if (this.filteredCities.length > 0) {
        this.showDropdown();
        this.renderDropdown();
      } else {
        this.showDropdown();
        this.renderNoResults();
      }
    }

    renderDropdown() {
      const fragment = document.createDocumentFragment();
      const query = this.input.value.trim().toLowerCase();

      this.filteredCities.forEach((city, index) => {
        const item = document.createElement('div');
        item.className = 'store-search-dropdown-item';
        item.setAttribute('role', 'option');
        item.setAttribute('aria-selected', 'false');
        item.setAttribute('data-index', index);

        const cityName = city.name;
        const score = this.filteredScores ? this.filteredScores[index] : 60;
        const matchIndex = cityName.toLowerCase().indexOf(query);

        if (matchIndex !== -1 && score >= 60) {
          // Contiguous match — highlight the matching substring
          const before = cityName.substring(0, matchIndex);
          const match = cityName.substring(matchIndex, matchIndex + query.length);
          const after = cityName.substring(matchIndex + query.length);
          item.innerHTML = `${escapeHtml(before)}<strong>${escapeHtml(match)}</strong>${escapeHtml(after)}`;
        } else if (score >= 40 && score < 60) {
          // Subsequence match — highlight individual matching chars
          item.innerHTML = this.highlightSubsequence(cityName, query);
        } else {
          // Fuzzy / typo match — no highlight, show as suggestion
          item.textContent = cityName;
        }

        item.addEventListener('click', () => this.selectCity(city));
        item.addEventListener('mouseenter', () => {
          this.selectedIndex = index;
          this.updateDropdownSelection();
        });

        fragment.appendChild(item);
      });

      this.dropdown.innerHTML = '';
      this.dropdown.appendChild(fragment);
      this.selectedIndex = -1;
    }

    highlightSubsequence(cityName, query) {
      const name = cityName.toLowerCase();
      const q = query.toLowerCase();
      let result = '';
      let qi = 0;
      for (let i = 0; i < cityName.length; i++) {
        if (qi < q.length && name[i] === q[qi]) {
          result += `<strong>${escapeHtml(cityName[i])}</strong>`;
          qi++;
        } else {
          result += escapeHtml(cityName[i]);
        }
      }
      return result;
    }

    renderNoResults() {
      this.dropdown.innerHTML = '<div class="store-search-no-results">No cities found</div>';
      this.selectedIndex = -1;
    }

    updateDropdownSelection() {
      const items = this.dropdown.querySelectorAll('.store-search-dropdown-item');
      items.forEach((item, index) => {
        item.classList.toggle('highlight', index === this.selectedIndex);
        item.setAttribute('aria-selected', index === this.selectedIndex);
      });
    }

    selectCity(city) {
      this.input.value = city.name;
      this.updateIconVisibility();
      this.hideDropdown();
      this.onSelect(city);
    }

    showDropdown() {
      this.dropdown.removeAttribute('hidden');
      this.container.setAttribute('aria-expanded', 'true');
    }

    hideDropdown() {
      this.dropdown.setAttribute('hidden', '');
      this.container.setAttribute('aria-expanded', 'false');
      this.selectedIndex = -1;
    }

    clear() {
      this.input.value = '';
      this.updateIconVisibility();
      this.hideDropdown();
      this.input.focus();
      this.filteredCities = this.cities;
      if (this.filteredCities.length > 0) {
        this.showDropdown();
        this.renderDropdown();
      }
      if (this.onSelect) {
        this.onSelect(null);
      }
    }
  }

  class MapModule {
    constructor(containerId, apiKey, options = {}) {
      this.containerId = containerId;
      this.apiKey = apiKey && apiKey.trim() ? apiKey.trim() : null;
      this.options = options;
      this.map = null;
      this.markers = [];
      this.infoWindows = [];
      this.markerIndexByStoreIndex = {};
      this.geocoder = null;
      this.apiLoaded = false;
      this.initialized = false;

      if (!this.apiKey) {
        this.showFallback();
        return;
      }

      if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', () => this.loadApi());
      } else {
        this.loadApi();
      }
    }

    loadApi() {
      if (window.google && window.google.maps) {
        this.apiLoaded = true;
        setTimeout(() => this.initMap(), 100);
        return;
      }

      const existingScript = document.querySelector('script[src*="maps.googleapis.com"]');
      if (existingScript) {
        let attempts = 0;
        const checkInterval = setInterval(() => {
          attempts++;
          if (window.google && window.google.maps) {
            clearInterval(checkInterval);
            this.apiLoaded = true;
            setTimeout(() => this.initMap(), 100);
          } else if (attempts >= 50) {
            clearInterval(checkInterval);
            this.showFallback();
          }
        }, 100);
        return;
      }

      const script = document.createElement('script');
      script.src = `https://maps.googleapis.com/maps/api/js?key=${this.apiKey}&libraries=geometry,places`;
      script.async = true;
      script.defer = true;
      script.onload = () => {
        this.apiLoaded = true;
        setTimeout(() => this.initMap(), 100);
      };
      script.onerror = () => this.showFallback();
      window.gm_authFailure = () => this.showFallback();
      document.head.appendChild(script);
    }

    initMap() {
      if (this.initialized) return;
      if (!window.google?.maps) {
        this.showFallback();
        return;
      }

      const container = document.getElementById(this.containerId);
      if (!container) {
        this.showFallback();
        return;
      }

      try {
        this.geocoder = new google.maps.Geocoder();
        this.map = new google.maps.Map(container, {
          zoom: this.options.zoom || 10,
          center: { lat: 28.6139, lng: 77.2090 },
          mapTypeControl: false,
          fullscreenControl: true,
          streetViewControl: false,
          zoomControl: true,
          styles: this.options.styles || []
        });
        this.initialized = true;

        google.maps.event.addDomListener(window, 'resize', debounce(() => {
          if (this.map) google.maps.event.trigger(this.map, 'resize');
        }, 250));

        setTimeout(() => {
          if (this.map) google.maps.event.trigger(this.map, 'resize');
        }, 300);
      } catch {
        this.showFallback();
      }
    }

    geocodeAddress(address) {
      return new Promise((resolve, reject) => {
        if (!this.geocoder) {
          reject(new Error('Geocoder not initialized'));
          return;
        }
        const clean = address.replace(/<[^>]*>/g, '');
        this.geocoder.geocode({ address: clean }, (results, status) => {
          if (status === 'OK' && results[0]) resolve(results[0].geometry.location);
          else reject(new Error(`Geocoding failed: ${status}`));
        });
      });
    }

    async addMarkers(stores, onMarkerClick) {
      if (!this.map || !this.apiLoaded) {
        await this.waitForMap();
      }

      this.clearMarkers();
      const bounds = new google.maps.LatLngBounds();
      const pinPath = 'M13.29,1.15c-16.33,6.44,1.02,35.26,4.22,40.33.23.36.75.36.98,0,3.2-5.06,20.55-33.89,4.22-40.33C19.7-.04,16.3-.04,13.29,1.15ZM18,21.12h0c-3.13,0-6.02-1.94-6.98-4.92-1.66-5.12,2.11-9.84,6.98-9.84h0c3.13,0,6.02,1.94,6.98,4.92,1.66,5.12-2.11,9.83-6.98,9.83Z';

      await Promise.all(stores.map((store, index) =>
        this.geocodeAddress(store.address).then(location => {
          const marker = new google.maps.Marker({
            position: location,
            map: this.map,
            title: store.name,
            animation: google.maps.Animation.DROP,
            icon: {
              path: pinPath,
              fillColor: '#ff9047',
              fillOpacity: 1,
              strokeColor: '#FFFFFF',
              strokeWeight: 1.5,
              scale: 1,
              anchor: new google.maps.Point(18, 42)
            }
          });

          const infoWindow = new google.maps.InfoWindow({
            content: this.createInfoWindowContent(store)
          });

          marker.addListener('click', () => {
            this.closeAllInfoWindows();
            infoWindow.open(this.map, marker);
            if (onMarkerClick) onMarkerClick(index);
          });

          const markerIndex = this.markers.length;
          this.markers.push(marker);
          this.infoWindows.push(infoWindow);
          this.markerIndexByStoreIndex[index] = markerIndex;
          bounds.extend(location);
        }).catch(() => null)
      ));

      if (this.markers.length > 0) {
        this.map.fitBounds(bounds);
        const listener = google.maps.event.addListener(this.map, 'bounds_changed', () => {
          if (this.map.getZoom() > 15) this.map.setZoom(15);
          google.maps.event.removeListener(listener);
        });
      }
    }

    createInfoWindowContent(store) {
      const safeDirections = sanitizeUrl(store.directions);
      return `
        <div style="padding:8px;min-width:200px;">
          <h3 style="margin:0 0 8px 0;font-size:16px;font-weight:600;">${escapeHtml(store.name || '')}</h3>
          <p style="margin:0 0 4px 0;font-size:13px;color:#666;">${escapeHtml(store.address || '')}</p>
          <p style="margin:0 0 4px 0;font-size:13px;color:#666;">${escapeHtml(store.phone || '')}</p>
          ${safeDirections && safeDirections !== '#' ? `<a href="${safeDirections}" target="_blank" rel="noopener" style="font-size:12px;color:#F7914B;text-decoration:none;">Get Directions →</a>` : ''}
        </div>`;
    }

    highlightMarker(index) {
      const markerIndex = this.markerIndexByStoreIndex[index];
      if (typeof markerIndex !== 'number') return;

      const marker = this.markers[markerIndex];
      if (!marker) return;

      this.closeAllInfoWindows();
      const infoWindow = this.infoWindows[markerIndex];
      if (infoWindow) infoWindow.open(this.map, marker);
      this.map.setCenter(marker.getPosition());
      this.map.setZoom(14);
    }

    closeAllInfoWindows() {
      this.infoWindows.forEach(iw => iw.close());
    }

    clearMarkers() {
      this.markers.forEach(m => m.setMap(null));
      this.infoWindows.forEach(iw => iw.close());
      this.markers = [];
      this.infoWindows = [];
      this.markerIndexByStoreIndex = {};
    }

    waitForMap() {
      return new Promise((resolve) => {
        const checkInterval = setInterval(() => {
          if (this.map && this.apiLoaded) {
            clearInterval(checkInterval);
            resolve();
          }
        }, 100);
      });
    }

    showFallback() {
      const container = document.getElementById(this.containerId);
      const fallback = document.getElementById(this.containerId.replace('container', 'fallback'));
      if (container) container.style.display = 'none';
      if (fallback) {
        fallback.removeAttribute('hidden');
        const content = fallback.querySelector('.store-map-fallback-content p');
        if (content) content.textContent = this.apiKey
          ? 'Map failed to load. Please check your API key and try again.'
          : 'Google Maps API key is required. Please add it in section settings.';
      }
      const rightPanel = fallback?.closest('.store-locator-right-panel');
      if (rightPanel) rightPanel.classList.add('map-fallback-active');
    }
  }

  class CardRenderer {
    constructor(containerId, onCardClick) {
      this.containerId = containerId;
      this.onCardClick = onCardClick;
      this.activeIndex = -1;
    }

    render(stores) {
      const container = document.getElementById(this.containerId);
      if (!container) return;
      if (!stores || stores.length === 0) {
        container.innerHTML = '<div class="store-empty-state">No stores found</div>';
        return;
      }
      const fragment = document.createDocumentFragment();
      stores.forEach((store, index) => fragment.appendChild(this.createCard(store, index)));
      container.innerHTML = '';
      container.appendChild(fragment);
    }

    createCard(store, index) {
      const card = document.createElement('div');
      card.className = 'store-card';
      card.setAttribute('data-store-index', index);
      card.setAttribute('tabindex', '0');
      card.setAttribute('role', 'button');
      card.setAttribute('aria-label', `Store: ${store.name}`);

      const safeDirections = sanitizeUrl(store.directions);

      card.innerHTML = `
        <h3 class="store-card-name">${escapeHtml(store.name || '')}</h3>
        <div class="store-card-info">
          <div class="store-card-info-item"><strong>Address:</strong><br>${escapeHtml(store.address || '')}</div>
          <div class="store-card-info-item"><strong>Phone:</strong> ${escapeHtml(store.phone || '')}</div>
          <div class="store-card-info-item"><strong>Hours:</strong><br>${escapeHtml(store.hours || '')}</div>
        </div>
        <div class="store-card-actions">
          ${safeDirections && safeDirections !== '#'
          ? `<a href="${safeDirections}" class="store-card-directions-btn" target="_blank" rel="noopener" aria-label="${escapeHtml(`Get directions to ${store.name}`)}">Get Directions</a>`
          : '<span class="store-card-directions-btn" style="opacity:0.5;cursor:not-allowed;">Directions unavailable</span>'
        }
        </div>`;

      card.addEventListener('click', () => {
        this.setActive(index);
        if (this.onCardClick) this.onCardClick(index);
      });

      card.addEventListener('keydown', (e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          card.click();
        }
      });

      return card;
    }

    setActive(index) {
      const cards = document.querySelectorAll(`#${this.containerId} .store-card`);
      cards.forEach((card, i) => card.classList.toggle('active', i === index));
      this.activeIndex = index;
      if (cards[index]) cards[index].scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }

    clearActive() {
      document.querySelectorAll(`#${this.containerId} .store-card`).forEach(c => c.classList.remove('active'));
      this.activeIndex = -1;
    }

    showError(message) {
      const container = document.getElementById(this.containerId);
      if (container) container.innerHTML = `<div class="store-error-state">${escapeHtml(message)}</div>`;
    }
  }

  class StoreLocatorController {
    constructor(sectionId, config) {
      this.sectionId = sectionId;
      this.config = config;
      this.state = {
        allLocations: [],
        filteredLocations: []
      };
      this.cities = [];
      this.searchModule = null;
      this.mapModule = null;
      this.cardRenderer = null;
      this.init();
    }

    async init() {
      try {
        await this.loadStoresData();
        this.extractCities();
        this.initModules();
        this.renderInitialView();
      } catch {
        if (this.cardRenderer) this.cardRenderer.showError('Failed to initialize store locator. Please refresh the page.');
      }
    }

    async loadStoresData() {
      const response = await fetch(this.config.storesDataUrl);
      if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
      const data = await response.json();

      this.state.allLocations = [];
      Object.keys(data).forEach(cityHandle => {
        if (Array.isArray(data[cityHandle])) {
          data[cityHandle].forEach(store => {
            this.state.allLocations.push({ ...store, cityHandle });
          });
        }
      });
    }

    extractCities() {
      const cityMap = new Map();
      const unmatched = [];

      this.state.allLocations.forEach(location => {
        if (!cityMap.has(location.cityHandle)) {
          const cityBlock = this.config.cityBlocks.find(b => b.settings.city_handle === location.cityHandle);
          if (cityBlock) {
            cityMap.set(location.cityHandle, { handle: location.cityHandle, name: cityBlock.settings.city_name, stores: [] });
          } else {
            cityMap.set('other', cityMap.get('other') || { handle: 'other', name: 'Other Locations', stores: [] });
            unmatched.push(location);
          }
        }
        const city = cityMap.get(location.cityHandle) || cityMap.get('other');
        if (city) city.stores.push(location);
      });

      this.cities = Array.from(cityMap.values());
    }

    initModules() {
      const searchContainer = document.querySelector(`#store-locator-${this.sectionId} .store-search-wrapper`);
      if (searchContainer) {
        searchContainer.querySelector('.store-search-dropdown')?.setAttribute('hidden', '');
        this.searchModule = new SearchModule(searchContainer, this.cities, (city) => this.handleCitySelect(city), this.config.apiKey);
      }

      this.mapModule = new MapModule(
        `store-map-container-${this.sectionId}`,
        this.config.apiKey,
        { zoom: 12, styles: [] }
      );

      this.cardRenderer = new CardRenderer(`store-cards-wrapper-${this.sectionId}`, (index) => this.handleCardClick(index));
    }

    renderInitialView() {
      this.state.filteredLocations = this.state.allLocations;
      this.cardRenderer.render(this.state.filteredLocations);
      if (this.mapModule) {
        this.mapModule.addMarkers(this.state.filteredLocations, (index) => this.handleCardClick(index));
      }
    }

    handleCitySelect(city) {
      this.state.filteredLocations = city ? city.stores : this.state.allLocations;
      this.cardRenderer.render(this.state.filteredLocations);
      this.cardRenderer.clearActive();
      if (this.mapModule) {
        this.mapModule.addMarkers(this.state.filteredLocations, (index) => this.handleCardClick(index));
      }
    }

    handleCardClick(index) {
      this.cardRenderer.setActive(index);
      if (this.mapModule) this.mapModule.highlightMarker(index);
    }
  }

  function initializeStoreLocator() {
    const sectionElement = document.querySelector('[data-store-locator-section]');
    if (!sectionElement || sectionElement.storeLocatorInitialized) return;

    sectionElement.storeLocatorInitialized = true;

    const sectionId = sectionElement.getAttribute('data-section-id');
    const apiKey = sectionElement.getAttribute('data-api-key') || '';
    const storesDataUrl = sectionElement.getAttribute('data-stores-url');

    let cityBlocks = [];
    try { cityBlocks = JSON.parse(sectionElement.getAttribute('data-city-blocks') || '[]'); } catch {}

    sectionElement.storeLocatorController = new StoreLocatorController(sectionId, { apiKey, cityBlocks, storesDataUrl });
  }

  const sectionElement = document.querySelector('[data-store-locator-section]');
  if (sectionElement) {
    if (typeof IntersectionObserver !== 'undefined') {
      const observer = new IntersectionObserver((entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            observer.unobserve(entry.target);
            initializeStoreLocator();
          }
        });
      }, { rootMargin: '50px' });
      observer.observe(sectionElement);
    } else {
      if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', initializeStoreLocator);
      } else {
        initializeStoreLocator();
      }
    }
  }
})();